import React, { FC, useEffect, useState } from 'react';
import { FormProvider, SubmitErrorHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useRouter } from 'next/router';
import { useToast } from '@common/toast';
import UserIdentificationType from '@common/inputFields/UserIdentificationType';
import Icon from '@common/assets';
import { Name, BirthDate, MobileWrap, Mail } from '@components/common/inputFields';
import BackgroundArea from '@components/profile/profileOverlay/overlays/common/backgroundArea';
import ExitModal from '@components/profile/profileOverlay/exitModal';
import useProfileBasic, {
  useFetchSaveProfileBasic,
  keys as profileBasicKeys,
} from '@repository/profiles/useProfileBasic';
import { useUserInfo } from '@repository/auth/useUserInfo';
import ProfileImage from './ProfileImage';
import profileBasicSchema from './profileBasicSchema';
import { ProfileBasicFormVO } from '@domain/vo/profile/ProfileBasicFormVO';
import Colors from '@domain/constant/colors';
import { queryClient } from '@utils/queryClient';
import { PATTERN } from '@utils/validators/pattern';
import { KeyboardEventUtil } from '@utils/KeyboardEventUtil';

import { getAtLeast15YearsOldDate } from '@utils/validators/validators';
import classnames from 'classnames/bind';
import styles from './basicInfo.module.scss';
const cx = classnames.bind(styles);

interface IBasicInfoOverlay {
  visible: boolean;
  onClose: () => void;
}

const profileBasicDefaultValues: RecursivePartial<ProfileBasicFormVO> = {
  profileImageUid: null,
  birthDate: '',
  countryCode: '82',
  mobile: '',
  email: '',
  token: '',
  userIdentificationType: null,
  identified: null,
};

const BasicInfoOverlay: FC<IBasicInfoOverlay> = ({ visible, onClose }) => {
  const Toast = useToast();
  const { query } = useRouter();
  const [exitModal, setExitModal] = useState(false);
  const [isSuccessMobileAuth, setIsSuccessMobileAuth] = useState(false);
  const [isSuccessMailAuth, setIsSuccessMailAuth] = useState(false);
  const { data: userInfo, refetch: refetchUserInfo } = useUserInfo();
  const { data: profileBasic, isLoading } = useProfileBasic(!!userInfo?.id && visible);

  const saveSuccess = async () => {
    await queryClient.refetchQueries([...profileBasicKeys, 'view'], { active: true, exact: true });

    //유저 정보 업데이트
    await refetchUserInfo();
    onClose();
    Toast({ type: 'success', iconType: 'info', content: '성공적으로 저장되었습니다.' });
  };

  const { mutate: saveProfileBasic } = useFetchSaveProfileBasic(saveSuccess);

  const methods = useForm<ProfileBasicFormVO>({
    mode: 'onChange',
    defaultValues: profileBasicDefaultValues as ProfileBasicFormVO,
    resolver: yupResolver(profileBasicSchema) as any,
  });

  const {
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { dirtyFields },
  } = methods;

  const identified = getValues('identified');
  const userIdentificationType = getValues('userIdentificationType');

  const getIsDisabled = () => {
    if (identified && userIdentificationType === 'SMS') return true;
    if (identified && userIdentificationType === 'IPIN') return true;
    else return false;
  };

  const onCloseOverlay = () => {
    // onChange로 default 값으로 바꾸었더라도 isDirty는 깊은 비교를 통해 검사하기 때문에 true가 된다.
    // mobile, email 값을 바꾸고 인증을 안하면 변경한 것으로 간주하지 않는다.
    const isChange = Object.keys(dirtyFields).filter((dirtyField) => {
      if ((dirtyField === 'mobile' || dirtyField === 'token') && !isSuccessMobileAuth) return false;
      else if (dirtyField === 'email' && !isSuccessMailAuth) return false;
      return true;
    }).length;

    if (isChange) {
      setExitModal(true);
      return;
    }

    onClose();
  };

  const onSubmit = () => {
    // 변경 인증 안한 경우, 원래 값으로 되돌리기 위함.
    if (!profileBasic) return;
    if (!getValues('name')?.match(PATTERN.NAME)) setValue('name', profileBasic.name);
    if (!getValues('birthDate')) setValue('birthDate', profileBasic.birthDate);
    if (!isSuccessMobileAuth) setValue('mobile', profileBasic.mobileText);
    if (!isSuccessMailAuth) setValue('email', profileBasic.email as any);

    saveProfileBasic(Object.assign(getValues(), { sourcePage: query.source_page }));
  };

  const onSubmitError: SubmitErrorHandler<ProfileBasicFormVO> = () => {
    Toast({ type: 'error', iconType: 'info', content: '저장에 필요한 정보를 작성해주세요.' });
  };

  useEffect(() => {
    if (profileBasic) {
      reset(new ProfileBasicFormVO(profileBasic));
    }
  }, [visible, profileBasic]);

  if (!profileBasic || isLoading) return null;

  return (
    <>
      <BackgroundArea visible={visible} onClickArea={onCloseOverlay} />
      <FormProvider {...methods}>
        <form
          onSubmit={(v) => handleSubmit(onSubmit, onSubmitError)(v)}
          onKeyDown={KeyboardEventUtil.onCheckKeyDownPreventEnterExceptTextarea}
        >
          <div className={cx('overlayArea', { on: visible })}>
            <div className={cx('overlayInner')}>
              <div className={cx('overlayTitleArea')}>
                <strong className={cx('overlayTitle')}>기본 정보를 확인해 주세요.</strong>
                <p className={cx('desc')}>
                  <Icon name="informationLight" width={20} height={20} fill={Colors.C_COOL_GRAY_70} />
                  이름, 휴대폰을 변경하고 싶다면 본인인증을 시도해 주세요.
                </p>
              </div>

              <div className={cx('scrollArea')}>
                <div className={cx('scrollInner')}>
                  <div className={cx('scrollWrap')}>
                    <ProfileImage name={'profileImageUid'} />

                    <div className={cx('infoArea')}>
                      <div className={cx('infoInner')}>
                        <div className={cx('infoTitleArea')}>
                          <em className={cx('infoTitle', 'required')}>이름</em>
                        </div>
                        {/* [D] 텍스트 타입은 'text' 클래스 추가 */}
                        <div className={cx('infoWrap')}>
                          <Name name={'name'} resetValue={profileBasic.name} disabled={getIsDisabled()} />
                        </div>
                      </div>

                      <div className={cx('infoInner')}>
                        <div className={cx('infoTitleArea')}>
                          {/* [D] 필수입력은 'required' 클래스 추가 */}
                          <em className={cx('infoTitle', 'required')}>생년월일</em>
                        </div>
                        <div className={cx('infoWrap')}>
                          <BirthDate
                            name={'birthDate'}
                            resetValue={profileBasic.birthDate}
                            maxDate={getAtLeast15YearsOldDate()}
                          />
                        </div>
                      </div>

                      <div className={cx('infoInner')}>
                        <div className={cx('infoTitleArea')}>
                          <em className={cx('infoTitle', 'required')}>이메일</em>
                        </div>
                        <div className={cx('infoWrap')}>
                          <Mail
                            name={'email'}
                            visible={visible}
                            isSuccessMailAuth={isSuccessMailAuth}
                            setIsSuccessMailAuth={setIsSuccessMailAuth}
                            resetValue={profileBasic.email}
                          />
                        </div>
                      </div>

                      <div className={cx('infoInner')}>
                        <div className={cx('infoTitleArea')}>
                          <em className={cx('infoTitle')}>휴대폰</em>
                          <span className={cx('tooltipArea')}>
                            <Icon name="informationLight" width={20} height={20} fill={Colors.C_COOL_GRAY_70} />
                            <div className={cx('tooltip')}>
                              휴대폰 번호가 없으면 <br />
                              채용 관련 알림이 가지 않아요.
                            </div>
                          </span>
                        </div>
                        <div className={cx('infoWrap')}>
                          <MobileWrap
                            name={'mobile'}
                            visible={visible}
                            setIsSuccessMobileAuth={setIsSuccessMobileAuth}
                            resetValue={profileBasic.mobile}
                          />
                        </div>
                      </div>

                      <div className={cx('infoInner')}>
                        <div className={cx('infoTitleArea')}>
                          <em className={cx('infoTitle')}>본인인증 방식</em>
                        </div>
                        <div className={cx('infoWrap')}>
                          <UserIdentificationType name={'userIdentificationType'} visible={visible} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className={cx('btnArea')}>
                <a className={cx('btn', 'cancel')} role="button" onClick={onCloseOverlay}>
                  나가기
                </a>
                <button className={cx('btn', 'confirm')} type="submit">
                  저장
                </button>
              </div>
            </div>

            {/* 나가기 모달 영역 */}
            <ExitModal visible={exitModal} onClose={() => setExitModal(false)} onExit={onClose} />
          </div>
        </form>
      </FormProvider>
    </>
  );
};

export default BasicInfoOverlay;
